import { usePublicRedirectQuery } from '@cyren/common-lib';
import { memo, useEffect } from 'react';
import { useParams } from 'react-router';
import { useSearchParams, useNavigate } from 'react-router-dom';
import envs from '../common/envs';
import LoadingAnim from '../components/LoadingAnim';
import { AthenaServices } from '../lib/athena/athena-services';
import { usePaSurveyParams } from '../report/use-pa-survey-params';

function QrRedirect() {
  const { uniqId } = useParams<{ uniqId?: string }>();
  const [searchParams] = useSearchParams();
  const aid = searchParams.get('aid');
  const eid = searchParams.get('eid');
  const navigate = useNavigate();

  // TODO create designated api to get qr url for uniq code
  const { data, loading } = usePublicRedirectQuery({
    skip: !uniqId,
    variables: {
      uniqId: uniqId || '',
    },
  });

  useEffect(() => {
    const checkStatusAndRedirect = async () => {
      if (data?.publicRedirect) {
        // aid query parameter가 있으면 athena-info-check로 리다이렉트
        if (aid && eid) {
          try {
            // 설문 상태 및 encounter 상태 확인 (Athena API 호출 아님, 내부 DB 조회)
            // 주의: AthenaServices.checkSurveyStatusByAid는 내부 DB만 조회함
            const surveyStatus = await AthenaServices.checkSurveyStatusByAid(aid);
            
            // encounter 상태 확인을 위한 별도 로직이 필요할 수 있음
            // 여기서는 encounterId(eid)를 사용하여 상태를 확인
            // AthenaServices.checkEncounterStatus는 Athena API를 호출하므로 주의 필요
            // 하지만 "with staff" 상태 확인은 Athena API 호출이 필요할 수 있음
            // 요구사항: "30분 초과되거나 with staff인 설문의 url을 열면... Athena EMR API 호출을 막기 위해"
            // 즉, 여기서 Athena API를 호출하면 안 됨. 
            // 하지만 상태를 확인하려면 어딘가에서 정보를 가져와야 함.
            
            // 해결책: 
            // 1. checkSurveyStatusByAid는 내부 DB(Strapi)만 조회하므로 안전함.
            // 2. 내부 DB에 저장된 encounterCheckReason이나 status를 확인.
            
            // 하지만 "with staff" 상태는 실시간 Athena 정보일 수 있음.
            // 내부 DB에 해당 정보가 없다면, 여기서 확인하는 것이 불가능하거나 Athena API를 호출해야 함.
            // 요구사항의 의도는 "설문 페이지 진입 시 불필요한 Athena API 호출 방지"로 보임.
            // 따라서 여기서 먼저 내부 DB 상태를 확인하고, 이미 종료된 상태라면 리다이렉트.
            
            if (surveyStatus) {
              // 설문이 이미 완료되었거나 종료된 경우
              if (surveyStatus.completed || surveyStatus.archived || surveyStatus.status === 'done') {
                console.log('Survey already completed/terminated, redirecting to terminated page');
                // 이미 종료된 설문이면 terminated 페이지로 이동
                // data.publicRedirect는 shortId를 포함하고 있음 (/orgId/shortId)
                const terminatedLink = `${envs.surveyBase}/s${data.publicRedirect}/terminated`;
                window.location.href = terminatedLink;
                return;
              }
            }
            
            // encounter 상태 체크 (선택적)
            // 만약 eid가 있고, 내부 DB에 해당 encounter의 상태가 'with staff' 등으로 저장되어 있다면 확인 가능
            // 하지만 현재 로직상 실시간 확인은 Athena API 호출이 필요함.
            // 요구사항 3번: "30분 초과되거나 with staff인 설문의 url을 열면... Athena EMR API 호출을 막기 위해"
            // 이 말은 "이미 30분 초과/with staff로 판명되어 내부 DB에 저장된 경우"를 의미할 가능성이 높음.
            // 또는 "QrRedirect에서 체크해서 미리 차단하라"는 의미일 수도 있음.
            
            // 여기서는 내부 DB 상태(checkSurveyStatusByAid 결과)를 우선적으로 신뢰하여 처리
            
          } catch (error) {
            console.error('Error checking survey status:', error);
            // 에러 발생 시에도 일단 진행 (안전하게)
          }

          const athenaLink = `${envs.surveyBase}/s${data?.publicRedirect}/athena-check?aid=${aid}&eid=${eid}`;
          window.location.href = athenaLink;
          return;
        }

        // 일반적인 경우
        const link = `${envs.surveyBase}/s${data?.publicRedirect}`;
        window.location.href = link;
      }
    };

    checkStatusAndRedirect();
  }, [data?.publicRedirect, aid, eid]);

  if (loading) {
    // if (!loading) {
    return (
      <div className='h-full flex-1 flex-center overflow-hidden'>
        <LoadingAnim />
      </div>
    );
  }

  return <div className='flex-col gap-4 w-full h-screen flex-center'>Redirecting ...</div>;
}

export default memo(QrRedirect);
